import axios from 'axios';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth } from './use-auth';
import { useUser } from './use-user';

const noAuthorization = [
  'auth/login/',
  'auth/registration/',
  'v1/projects/',
  'auth/password/reset/',
  'auth/password/reset/confirm/',
  'login/magic/',
  'login/request-magic-link/',
];

const useAxios = () => {
  const { auth, setAuth } = useAuth();
  const { lang } = useUser();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const urlFlow = queryParams.get('flow');

  return useMemo(() => {
    const apiInstance = axios.create({
      baseURL: import.meta.env.VITE_API_URL,
    });

    apiInstance.interceptors.request.use(
      (config) => {
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Token ${auth}`;
        }
        if (noAuthorization.includes(config.url)) {
          config.headers.Authorization = null;
        }
        config.headers['Accept-Language'] = lang;
        return config;
      },
      (error) => Promise.reject(error)
    );

    apiInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error?.response?.status === 401) {
          setAuth(null);
        }
        return Promise.reject(error);
      }
    );

    apiInstance.event = async (event, flow) => {
      // event can be either a string or an array of strings
      const events = Array.isArray(event) ? event : [event];
      await events.reduce(async (previousPromise, e) => {
        await previousPromise;

        return apiInstance.post(`v1/events/`, {
          flow: urlFlow || flow || 'additional_onboarding_v4',
          event: e,
        });
      }, Promise.resolve());
    };

    return apiInstance;
  }, [auth, lang, setAuth, urlFlow]);
};

export default useAxios;
